import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Tooltip, Typography } from '@mui/material'
import AccordionSetup from '../AccordionSetup'
import AddCircleOutlineOutlined from '@mui/icons-material/AddCircleOutlineOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined'
import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EventDto } from '../../../interfaces/IEvent'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { enqueueSnackbar } from 'notistack'
import ViewAlert from './ViewAlert'
import AlertSnippet from './AlertSnippet'
import CustomerNotificationStatus from './CustomerNotificationStatus'
import DialogCloseButton from '../../../components/Buttons/DialogCloseButton'

interface AlertProps {
  viewEvent: EventDto
  onUpdateEventView: (eventId: string) => void
}

function AlertContainer({ viewEvent, onUpdateEventView }: AlertProps) {
  const navigate = useNavigate()
  const { context } = useContext(EbAuthenticatedAppContext)
  const [openAlert, setOpenAlert] = useState<boolean>(false)
  const [viewAlertId, setViewAlertId] = useState<string>('')
  const [openNotificationStatus, setOpenNotificationStatus] = useState<boolean>(false)

  //disableIfNoAffected. Handle null or empty affectedCustomers, affectedPhoneNumbers, isAllCompanyCustomersAffected, and affectedEmailAddresses
  //If all are empty or false, disable the accordion.

  const disableIfNoAffected = !(
    viewEvent.affectedCustomers?.length > 0 ||
    viewEvent.affectedPhoneNumbers?.length > 0 ||
    viewEvent.isAllCompanyCustomersAffected ||
    viewEvent.affectedEmailAddresses?.length > 0
  )

  const goToCreateAlert = () => {
    navigate(`/events/${viewEvent.id}/alerts/create`)
  }

  const deleteAlert = async (alertId: string) => {
    const response = await context.restApiClient.buildRequest(`/api/event/${viewEvent.id}/alert/${alertId}/cancel-alert`).deleteAsync()

    if (response.error) {
      return enqueueSnackbar(<Typography variant="body2">Kunne ikke slette varselet. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
    } else {
      enqueueSnackbar(<Typography variant="body2">Varsel er slettet.</Typography>, { variant: 'success' })
      return onUpdateEventView(viewEvent.id)
    }
  }

  const goToEditAlert = (alertId: string) => {
    navigate(`/events/${viewEvent.id}/alerts/${alertId}`)
  }

  const handleViewAlert = (alertId: string) => {
    setViewAlertId(alertId)
    setOpenAlert(true)
  }

  const handleCloseAlert = () => {
    setOpenAlert(false)
  }

  return (
    <>
      <AccordionSetup
        disabled={disableIfNoAffected}
        title="Varsler"
        icon={<NotificationsNoneOutlinedIcon />}
        actionIcon={<AddCircleOutlineOutlined />}
        action={goToCreateAlert}
        contentHeight={400}
        extraActions={
          viewEvent.affectedCustomers?.length > 0 ? (
            <Tooltip title="Varslingsstatus per mottaker">
              <IconButton size="small" onClick={() => setOpenNotificationStatus(true)} sx={{ marginLeft: 'auto' }}>
                <PersonSearchOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
      >
        {viewEvent.alerts && viewEvent.alerts.length > 0 ? (
          [...viewEvent.alerts].map((alert, index) => {
            return (
              <Box key={index}>
                <AlertSnippet
                  alertSnippet={alert}
                  eventId={viewEvent.id}
                  onDelete={deleteAlert}
                  onView={handleViewAlert}
                  onEdit={goToEditAlert}
                  onUpdate={onUpdateEventView}
                />
                {viewEvent.alerts.length - 1 !== index && <Divider sx={{ backgroundColor: 'divider' }} />}
              </Box>
            )
          })
        ) : (
          <Typography variant="body2">Ingen varsler.</Typography>
        )}
      </AccordionSetup>

      {viewAlertId && openAlert && (
        <ViewAlert
          open={openAlert}
          onClose={handleCloseAlert}
          alertId={viewAlertId}
          eventId={viewEvent.id}
          viewEvent={viewEvent}
          onPublished={() => onUpdateEventView(viewEvent.id)}
        />
      )}

      <Dialog open={openNotificationStatus} onClose={() => setOpenNotificationStatus(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Varslingsstatus per mottaker
          <DialogCloseButton handleClose={() => setOpenNotificationStatus(false)} />
        </DialogTitle>
        <DialogContent>
          <CustomerNotificationStatus affectedCustomers={viewEvent.affectedCustomers ?? []} alerts={viewEvent.alerts ?? []} affectedPhoneNumbers={viewEvent.affectedPhoneNumbers} affectedEmailAddresses={viewEvent.affectedEmailAddresses} />
        </DialogContent>
      </Dialog>
    </>
  )
}

export default AlertContainer
